import type {
  AeRespondentFraming,
  AeScaleDisplayStyle,
  AeTemplateDetail,
} from '@/types/assessmentEngine';
import {
  DEFAULT_SCALE_MAX_LABEL,
  DEFAULT_SCALE_MIN_LABEL,
  DEFAULT_NPS_MAX_LABEL,
  DEFAULT_NPS_MIN_LABEL,
} from './scaleUtils';
import { parseMaturityMap, type AeMaturityMapConfig } from './maturityMapUtils';

/** Built-in demographic field keys for template settings. */
export type AeDemographicFieldKey =
  | 'name'
  | 'email'
  | 'phone'
  | 'address'
  | 'title'
  | 'company_name'
  | 'position';

export interface AeDemographicFieldConfig {
  enabled: boolean;
  required: boolean;
}

export interface AeTemplateScalePreset {
  id: string;
  name: string;
  scale_min: number;
  scale_max: number;
  allow_na: boolean;
  /** Selecting this shared scale creates a matrix question instead of a scale question. */
  matrixShared?: boolean;
  scaleDisplayStyle?: AeScaleDisplayStyle;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
  /** Optional custom label for each scale point (index 0 = scale_min). */
  scaleValueLabels?: string[];
}

/** Scoring dimension — maps to a top-level section in survey design. */
export interface AeTemplateSubDimension {
  id: string;
  name: string;
  section_id: string;
}

export interface AeTemplateDimension {
  id: string;
  name: string;
  section_id: string;
  sub_dimensions?: AeTemplateSubDimension[];
}

export interface AeTemplateDemographicsConfig {
  placement: 'beginning' | 'end';
  fields: Record<AeDemographicFieldKey, AeDemographicFieldConfig>;
}

export interface AeTemplateDefaultConfig {
  distribution_mode?: string;
  captcha_required?: boolean;
  is_anonymous?: boolean;
  scale_min?: number;
  scale_max?: number;
  allow_na?: boolean;
  benchmark_b1_enabled?: boolean;
  dimensions?: AeTemplateDimension[];
  scale_presets?: AeTemplateScalePreset[];
  demographics?: AeTemplateDemographicsConfig;
  respondent_framing?: AeRespondentFraming;
  /** Optional maturity % bands; off when omitted or enabled=false. */
  maturity_map?: AeMaturityMapConfig;
}

/** True when template setup defines purpose or instructions for the opening screen. */
export function hasRespondentOpening(framing?: AeRespondentFraming | null): boolean {
  const purpose = framing?.opening?.purpose?.trim();
  const instructions = framing?.opening?.instructions?.trim();
  return !!(purpose || instructions);
}

export function parseRespondentFraming(raw: unknown): AeRespondentFraming | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const rf = raw as Record<string, unknown>;
  const openingRaw = rf.opening;
  const closingRaw = rf.closing;
  const framing: AeRespondentFraming = {};

  if (openingRaw && typeof openingRaw === 'object') {
    const opening = openingRaw as Record<string, unknown>;
    framing.opening = {
      purpose: typeof opening.purpose === 'string' ? opening.purpose : undefined,
      instructions: typeof opening.instructions === 'string' ? opening.instructions : undefined,
    };
  }

  if (closingRaw && typeof closingRaw === 'object') {
    const closing = closingRaw as Record<string, unknown>;
    framing.closing = {
      message: typeof closing.message === 'string' ? closing.message : undefined,
    };
  }

  return framing;
}

export const DEMOGRAPHICS_SECTION_CODE = 'SYS_DEMOGRAPHICS';

export const AE_DEMOGRAPHIC_FIELD_KEYS: AeDemographicFieldKey[] = [
  'name',
  'email',
  'phone',
  'address',
  'title',
  'company_name',
  'position',
];

export const DEFAULT_SCALE_PRESETS: AeTemplateScalePreset[] = [
  {
    id: 'scale_1_5',
    name: 'Standard 1–5',
    scale_min: 1,
    scale_max: 5,
    allow_na: true,
    scaleDisplayStyle: 'radio',
    scaleMinLabel: DEFAULT_SCALE_MIN_LABEL,
    scaleMaxLabel: DEFAULT_SCALE_MAX_LABEL,
  },
  {
    id: 'scale_1_7',
    name: 'Likert 1–7',
    scale_min: 1,
    scale_max: 7,
    allow_na: true,
    scaleDisplayStyle: 'radio',
    scaleMinLabel: DEFAULT_SCALE_MIN_LABEL,
    scaleMaxLabel: DEFAULT_SCALE_MAX_LABEL,
  },
  {
    id: 'nps_0_10',
    name: 'NPS 0–10',
    scale_min: 0,
    scale_max: 10,
    allow_na: false,
    scaleDisplayStyle: 'numbers',
    scaleMinLabel: DEFAULT_NPS_MIN_LABEL,
    scaleMaxLabel: DEFAULT_NPS_MAX_LABEL,
  },
];

function withDefaultScaleLabels(presets: AeTemplateScalePreset[]): AeTemplateScalePreset[] {
  return presets.map((preset) => ({
    ...preset,
    scaleMinLabel: preset.scaleMinLabel?.trim() || DEFAULT_SCALE_MIN_LABEL,
    scaleMaxLabel: preset.scaleMaxLabel?.trim() || DEFAULT_SCALE_MAX_LABEL,
  }));
}

export function defaultDemographicsFields(): Record<AeDemographicFieldKey, AeDemographicFieldConfig> {
  return {
    name: { enabled: false, required: false },
    email: { enabled: true, required: true },
    phone: { enabled: false, required: false },
    address: { enabled: false, required: false },
    title: { enabled: false, required: false },
    company_name: { enabled: false, required: false },
    position: { enabled: false, required: false },
  };
}

/** Parse ae_templates.default_config into a typed structure with defaults. */
export function parseTemplateDefaultConfig(
  raw: Record<string, unknown> | null | undefined
): AeTemplateDefaultConfig {
  const base = (raw ?? {}) as AeTemplateDefaultConfig;
  return {
    ...base,
    dimensions: Array.isArray(base.dimensions) ? base.dimensions : [],
    scale_presets: withDefaultScaleLabels(
      Array.isArray(base.scale_presets) && base.scale_presets.length > 0
        ? base.scale_presets
        : DEFAULT_SCALE_PRESETS
    ),
    demographics: {
      placement: base.demographics?.placement ?? 'beginning',
      fields: {
        ...defaultDemographicsFields(),
        ...(base.demographics?.fields ?? {}),
      },
    },
    respondent_framing: parseRespondentFraming(base.respondent_framing),
    maturity_map: parseMaturityMap(base.maturity_map),
  };
}

export function templateConfigFromDetail(template: AeTemplateDetail): AeTemplateDefaultConfig {
  return parseTemplateDefaultConfig(template.default_config as Record<string, unknown>);
}
